// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — Navegação: a tela aberta vive na URL (#214)
//
// Cada tela tem endereço próprio (/notas, /auditoria, /lubDashboard): o F5
// mantém a tela, o voltar do navegador volta para a anterior e o link de uma
// tela pode ser salvo ou mandado. Continua uma página só — trocar de tela
// reescreve a barra (pushState), não recarrega nada. O Cloudflare devolve o
// app em qualquer caminho (public/_redirects).
// ═══════════════════════════════════════════════════════════════════
import { useState, useCallback, useEffect, useRef, startTransition } from "react";

// Sem decodeURIComponent de propósito: os ids são ASCII, e um "%" solto na
// barra faria o decode lançar antes do ErrorBoundary existir.
const telaDaUrl = () => window.location.pathname.slice(1);

// Grupo dono da tela na URL. Cada tela pertence a um grupo só, então o F5
// em /notas sabe que é Gamma sem perguntar de novo (#232). Na raiz, ou num
// endereço que não é tela, devolve null e o seletor de grupo aparece.
// `grupos` = TELAS da Sidebar: { gamma: { ids }, lubpress: { ids } }.
export const grupoDaUrl = (grupos) =>
  Object.keys(grupos).find((g) => grupos[g].ids.includes(telaDaUrl())) || null;

// `telas` = { inicial, ids } do grupo escolhido; antes de escolher (login),
// undefined — e aí a URL fica como veio, para o link abrir depois do login.
export function useNavegacao(telas) {
  const [pedida, setPedida] = useState(telaDaUrl);
  // Endereço que não é tela do grupo — a raiz, tela que não existe, tela do
  // outro grupo depois de "Trocar grupo" — abre a inicial.
  const page = telas && (telas.ids.includes(pedida) ? pedida : telas.inicial);

  // Uma tela com trabalho não salvo (Auditoria de Faturas, #184) registra
  // aqui quem segura a saída: recebe a ação e devolve true se a guardou
  // para depois do pop-up. Toda saída passa por `sair`, que devolve false
  // quando a saída ficou segura.
  const guardaSaida = useRef(null);
  const sair = useCallback((acao) => {
    if (guardaSaida.current?.(acao)) return false;
    acao();
    return true;
  }, []);

  // Cada módulo é lazy. Trocar de tela num setState comum faz o React
  // suspender em resposta a input síncrono — e isso não cai no fallback do
  // Suspense, cai no ErrorBoundary: "Erro ao carregar o sistema" no lugar da
  // tela. Com startTransition o React sabe que pode esperar e mostra o
  // esqueleto de página enquanto o pedaço chega (#101).
  const abrir = useCallback((destino) => startTransition(() => setPedida(destino)), []);
  // Devolve false se o pop-up segurou a troca: o menu não marca a tela
  // escolhida antes da hora (#313).
  const setPage = useCallback(
    (destino) => destino === page || sair(() => abrir(destino)),
    [page, sair, abrir]
  );

  // A barra segue a tela, depois que ela abriu. Tela escolhida empilha;
  // endereço corrigido (raiz, tela de outro grupo) substitui, para o voltar
  // não cair nele de novo.
  useEffect(() => {
    if (!page || telaDaUrl() === page) return;
    window.history[pedida === page ? "pushState" : "replaceState"](null, "", `/${page}`);
  }, [page, pedida]);

  // Voltar/avançar do navegador: quando o evento chega, a barra já mudou.
  // Com trabalho não salvo, ela volta a mostrar a tela atual enquanto o
  // pop-up decide; se a pessoa sair, o efeito acima empilha o destino.
  useEffect(() => {
    const aoVoltar = () => {
      const destino = telaDaUrl();
      if (guardaSaida.current?.(() => abrir(destino)))
        window.history.pushState(null, "", `/${page}`);
      else abrir(destino);
    };
    window.addEventListener("popstate", aoVoltar);
    return () => window.removeEventListener("popstate", aoVoltar);
  }, [page, abrir]);

  return { page, setPage, sair, guardaSaida };
}
